import { useCallback, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../services/firebase";
import { useChildContext } from "../contexts/ChildContext";
import { useRole } from "../contexts/RoleContext";
import { listenForFollowUps, initializeNotificationsForPendingFollowUps, processQuickResponses, startQuickResponseListener } from "../services/followUpService";
import { analyzeOtherIncidentPatterns } from "../services/incidentService";
import { getE2EMockDashboardState, isE2EMockEnabled } from "../services/e2eMock";
import {
  applyPanelDashboardOptimisticEdit,
  applyPanelDashboardOptimisticEntry,
} from "./panelDashboard/panelDashboardOptimisticUpdates";
import { applyPanelDashboardEditRoute, buildPanelDashboardEditEntry } from "./panelDashboard/panelDashboardEditRouting";
import { applyPanelDashboardQuickEntryCreate } from "./panelDashboard/panelDashboardQuickEntryUpdates";
import { usePanelDashboardActions } from "./panelDashboard/usePanelDashboardActions";
import { usePanelDashboardModalState } from "./panelDashboard/usePanelDashboardModalState";
import { usePanelDashboardTimelineFeed } from "./panelDashboard/usePanelDashboardTimelineFeed";
import { formatTimeForSummary, toEntryDate } from "./panelDashboard/panelDashboardTimelineUtils";
import { fetchMedications } from "../services/medicationService";

const hasMedicationProfileValue = (child = {}) => {
  const medicationRecords = Array.isArray(child.medicationRecords)
    ? child.medicationRecords
    : [];

  return medicationRecords.some((entry) => entry?.name && !entry?.isArchived);
};

const hydrateChildrenProfileCompletionContext = async (children = []) => (
  Promise.all(children.map(async (child) => {
    if (!child?.id || hasMedicationProfileValue(child)) {
      return {
        ...child,
        careProfileHasActiveMedications: hasMedicationProfileValue(child),
      };
    }

    try {
      const medications = await fetchMedications(child.id);
      return {
        ...child,
        medicationRecords: medications,
        careProfileHasActiveMedications: medications.some((entry) => entry?.name && !entry?.isArchived),
      };
    } catch (error) {
      console.error(`Error loading medication presence for ${child.name || child.id}:`, error);
      return child;
    }
  }))
);

export const usePanelDashboard = ({ activeChildOnly = false } = {}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const isMockMode = isE2EMockEnabled();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const {
    loading: roleLoading,
    childrenWithAccess,
    getUserRoleForChild,
    canAddDataForChild,
    isReadOnlyForChild,
    refreshRoles,
    USER_ROLES,
  } = useRole();

  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allEntries, setAllEntries] = useState({});
  const [recentEntries, setRecentEntries] = useState({});
  const [timelineSummary, setTimelineSummary] = useState({});
  const [incidents, setIncidents] = useState({}); // Store incidents by child ID
  const [expandedCards, setExpandedCards] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [highlightedActions] = useState({});
  const childrenAccessKey = (childrenWithAccess || []).map((child) => child.id).join('|');
  const childIdsKey = (children || []).map((child) => child.id).join('|');

  const {
    bathroomLogChild,
    bathroomLogEditEntry,
    dailyCareAction,
    dailyCareChild,
    dailyCareEditEntry,
    dailyHabitsChild,
    dailyHabitsEditEntry,
    dailyHabitsInitialCategoryId,
    entryType,
    followUpIncident,
    foodLogChild,
    foodLogEditEntry,
    handleCloseBathroomLogSheet,
    handleCloseDailyCareModal,
    handleCloseDailyHabitsModal,
    handleCloseFollowUpModal,
    handleCloseFoodLogSheet,
    handleCloseIncidentModal,
    handleCloseMedicalLog,
    handleCloseSmartCaptureNoteSheet,
    handleCloseTimelineNoteEditSheet,
    handleCloseMoodSheet,
    handleClosePatternSuggestionModal,
    handleCloseSleepLogSheet,
    handleEditMedicalLog,
    handleOpenBathroomLog,
    handleOpenFoodLog,
    handleOpenMedicalLog,
    handleOpenSleepLog,
    handleQuickDataEntry,
    handleTrack,
    incidentChild,
    incidentEditEntry,
    medicalLogChild,
    medicalLogEditEntry,
    smartCaptureNoteChild,
    smartCaptureNoteEditEntry,
    timelineNoteEditChild,
    timelineNoteEditEntry,
    moodSheetChild,
    moodSheetEntry,
    patternSuggestions,
    quickEntryDate,
    quickEntryAutoSubmit,
    quickEntryEditEntry,
    quickEntryMode,
    quickEntryStep,
    quickEntryInitialAction,
    quickEntryInitialPhotoFile,
    quickEntryInitialPhotoIntent,
    quickEntryInitialText,
    selectedChild,
    setBathroomLogChild,
    setBathroomLogEditEntry,
    setDailyCareAction,
    setDailyCareChild,
    setDailyCareEditEntry,
    setDailyHabitsChild,
    setDailyHabitsEditEntry,
    setDailyHabitsInitialCategoryId,
    setEntryType,
    setFollowUpIncident,
    setFoodLogChild,
    setFoodLogEditEntry,
    setIncidentChild,
    setIncidentEditEntry,
    setMoodSheetChild,
    setMoodSheetEntry,
    setPatternSuggestions,
    setQuickEntryDate,
    setQuickEntryAutoSubmit,
    setQuickEntryInitialAction,
    setQuickEntryInitialPhotoFile,
    setQuickEntryInitialText,
    setQuickEntryEditEntry,
    setQuickEntryMode,
    setQuickEntryStep,
    setSelectedChild,
    setShowBathroomLogSheet,
    setShowDailyCareModal,
    setShowDailyHabitsModal,
    setShowFollowUpModal,
    setShowFoodLogSheet,
    setShowIncidentModal,
    setShowMedicalLogPanel,
    setShowSmartCaptureNoteSheet,
    setShowTimelineNoteEditSheet,
    setShowMoodSheet,
    setShowPatternSuggestionModal,
    setShowQuickEntry,
    setShowSleepLogSheet,
    setSleepLogChild,
    setSleepLogEditEntry,
    setSleepLogType,
    setSmartCaptureNoteChild,
    setSmartCaptureNoteEditEntry,
    setTimelineNoteEditChild,
    setTimelineNoteEditEntry,
    setSuggestionsChildId,
    showBathroomLogSheet,
    showDailyCareModal,
    showDailyHabitsModal,
    showFollowUpModal,
    showFoodLogSheet,
    showIncidentModal,
    showMedicalLogPanel,
    showSmartCaptureNoteSheet,
    showTimelineNoteEditSheet,
    showMoodSheet,
    showPatternSuggestionModal,
    showQuickEntry,
    showSleepLogSheet,
    sleepLogChild,
    sleepLogEditEntry,
    sleepLogType,
    suggestionsChildId,
  } = usePanelDashboardModalState({
    canAddDataForChild,
    checkForPatterns: async (childId) => {
      try {
        const suggestions = await analyzeOtherIncidentPatterns(childId);
        if (suggestions.length > 0) {
          setPatternSuggestions(suggestions);
          setSuggestionsChildId(childId);
          setShowPatternSuggestionModal(true);
        }
      } catch (error) {
        console.error('Error checking for patterns:', error);
      }
    },
    setCurrentChildId,
  });

  const {
    formatTimeAgo,
    getTypeConfig,
    handleAddChild,
    handleAddChildSuccess,
    handleCreateCustomCategories,
    handleDailyReport,
    handleDeleteChild,
    handleEditChild,
    handleReviewChild,
    handleEditChildSuccess,
    handleGroupActionClick,
    handleInviteTeamMember,
    handleMessages,
    handleShowCareReport,
    refreshDashboard,
  } = usePanelDashboardActions({
    navigate,
    refreshRoles,
    setCurrentChildId,
    setDailyCareAction,
    setDailyCareChild,
    setFoodLogChild,
    setIncidentChild,
    setShowDailyCareModal,
    setShowFoodLogSheet,
    setShowIncidentModal,
    setShowSleepLogSheet,
    setSleepLogChild,
    setSleepLogType,
    theme,
  });

  useEffect(() => {
    if (isMockMode) {
      return;
    }

    // Start SW quick response listener once on mount
    startQuickResponseListener();

    if (roleLoading) return;
    let isActive = true;

    const loadChildren = async () => {
      try {
        const childrenWithRoles = childrenWithAccess || [];
        const childrenWithCompletionContext = await hydrateChildrenProfileCompletionContext(childrenWithRoles);

        if (!isActive) {
          return;
        }

        setChildren(childrenWithCompletionContext);

        // Initialize notifications for pending follow-ups
        if (childrenWithCompletionContext.length > 0) {
          initializeNotificationsForPendingFollowUps(childrenWithCompletionContext);

          // Process any quick responses from notifications
          processQuickResponses().then(processedResponses => {
            if (processedResponses.length > 0) {
              console.log(`📱 Processed ${processedResponses.length} quick responses from notifications`);
              // Could show a toast or update UI to reflect the processed responses
            }
          });
        }
      } catch (error) {
        console.error("Error loading children:", error);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadChildren();

    return () => {
      isActive = false;
    };
  }, [isMockMode, roleLoading, childrenAccessKey, childrenWithAccess]);

  useEffect(() => {
    if (isMockMode) {
      return;
    }

    if (!currentChildId && children.length > 0) {
      setCurrentChildId(children[0].id);
    }
  }, [currentChildId, children, setCurrentChildId, isMockMode]);

  usePanelDashboardTimelineFeed({
    activeChildOnly,
    childIdsKey,
    children,
    currentChildId,
    getUserRoleForChild,
    isMockMode,
    setAllEntries,
    setIncidents,
    setRecentEntries,
    setTimelineSummary,
  });

  useEffect(() => {
    if (isMockMode) {
      return;
    }

    if (!children.length) {
      return undefined;
    }

    const handleTimelineEntryCreated = (event) => {
      applyPanelDashboardOptimisticEntry({
        entry: event?.detail,
        allEntries,
        setAllEntries,
        setIncidents,
        setRecentEntries,
        setTimelineSummary,
      });
    };

    window.addEventListener('captureez:timeline-entry-created', handleTimelineEntryCreated);
    return () => window.removeEventListener('captureez:timeline-entry-created', handleTimelineEntryCreated);
  }, [children, allEntries, isMockMode]);

  // Listen for follow-up reminders
  useEffect(() => {
    if (isMockMode) {
      return;
    }

    if (children.length === 0) return;

    const childIds = children.map(child => child.id);

    const handleFollowUpNeeded = (incident) => {
      setFollowUpIncident(incident);
      setShowFollowUpModal(true);
    };

    const cleanup = listenForFollowUps(childIds, handleFollowUpNeeded);

    return cleanup;
  }, [children, isMockMode, setFollowUpIncident, setShowFollowUpModal]);

  const handleQuickEntryComplete = (entry) => {
    if (quickEntryMode === 'edit' && quickEntryEditEntry?.id) {
      setQuickEntryMode('create');
      setQuickEntryEditEntry(null);
      setShowQuickEntry(false);
      setSelectedChild(null);
      setQuickEntryDate(null);
      setQuickEntryAutoSubmit(false);
      setQuickEntryInitialAction(null);
      setQuickEntryInitialPhotoFile(null);
      setQuickEntryInitialText('');
      setQuickEntryStep(0);
      window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
        detail: {
          childId: selectedChild?.id || quickEntryEditEntry.childId,
          entryId: quickEntryEditEntry.id,
          collection: 'dailyLogs',
        },
      }));
      return;
    }

    applyPanelDashboardQuickEntryCreate({
      entry,
      selectedChild,
      setAllEntries,
      setRecentEntries,
      setTimelineSummary,
    });

    setShowQuickEntry(false);
    setSelectedChild(null);
    setQuickEntryDate(null);
    setQuickEntryAutoSubmit(false);
    setQuickEntryInitialAction(null);
    setQuickEntryInitialPhotoFile(null);
    setQuickEntryInitialText('');
  };

  const handleTimelineNoteEditComplete = (entry) => {
    applyPanelDashboardOptimisticEdit({
      entry,
      setAllEntries,
      setRecentEntries,
    });
  };

  const handleQuickEntrySkip = () => {
    setShowQuickEntry(false);
    setSelectedChild(null);
    setQuickEntryDate(null);
    setQuickEntryAutoSubmit(false);
    setQuickEntryInitialAction(null);
    setQuickEntryInitialPhotoFile(null);
    setQuickEntryInitialText('');
    setQuickEntryMode('create');
    setQuickEntryEditEntry(null);
  };

  const handleTimelineEditRequest = useCallback((event) => {
    const entry = event?.detail?.entry;
    const entryConfig = event?.detail?.entryConfig || null;
    if (!entry) {
      return;
    }

    const editEntry = buildPanelDashboardEditEntry(entry, entryConfig);

    const childId = entry.childId || entry.child?.id || null;
    const child = children.find((item) => item.id === childId) || null;
    if (!child) {
      return;
    }

    applyPanelDashboardEditRoute({
      child,
      editEntry,
      entryConfig,
      handleEditMedicalLog,
      setBathroomLogChild,
      setBathroomLogEditEntry,
      setCurrentChildId,
      setDailyCareAction,
      setDailyCareChild,
      setDailyCareEditEntry,
      setFoodLogChild,
      setFoodLogEditEntry,
      setIncidentChild,
      setIncidentEditEntry,
      setShowSmartCaptureNoteSheet,
      setMoodSheetChild,
      setMoodSheetEntry,
      setShowBathroomLogSheet,
      setShowDailyCareModal,
      setShowFoodLogSheet,
      setShowIncidentModal,
      setSmartCaptureNoteChild,
      setSmartCaptureNoteEditEntry,
      setShowMoodSheet,
      setShowSleepLogSheet,
      setSleepLogChild,
      setSleepLogEditEntry,
      setSleepLogType,
      setShowTimelineNoteEditSheet,
      setTimelineNoteEditChild,
      setTimelineNoteEditEntry,
    });
  }, [
    children,
    handleEditMedicalLog,
    setBathroomLogChild,
    setBathroomLogEditEntry,
    setCurrentChildId,
    setDailyCareAction,
    setDailyCareChild,
    setDailyCareEditEntry,
    setFoodLogChild,
    setFoodLogEditEntry,
    setIncidentChild,
    setIncidentEditEntry,
    setMoodSheetChild,
    setMoodSheetEntry,
    setShowBathroomLogSheet,
    setShowDailyCareModal,
    setShowFoodLogSheet,
    setShowIncidentModal,
    setShowMoodSheet,
    setShowSleepLogSheet,
    setShowSmartCaptureNoteSheet,
    setShowTimelineNoteEditSheet,
    setSleepLogChild,
    setSleepLogEditEntry,
    setSleepLogType,
    setSmartCaptureNoteChild,
    setSmartCaptureNoteEditEntry,
    setTimelineNoteEditChild,
    setTimelineNoteEditEntry,
  ]);

  useEffect(() => {
    if (isMockMode) {
      return undefined;
    }

    window.addEventListener('captureez:timeline-edit-request', handleTimelineEditRequest);
    return () => window.removeEventListener('captureez:timeline-edit-request', handleTimelineEditRequest);
  }, [handleTimelineEditRequest, isMockMode]);

  const toggleCard = (childId) => {
    setExpandedCards((prev) => ({ ...prev, [childId]: !prev[childId] }));
  };

  const isCardExpanded = (childId) => expandedCards[childId] || false;

  const ownChildren = children.filter(child => getUserRoleForChild?.(child.id) === USER_ROLES.CARE_OWNER);
  const familyChildren = children.filter(child => getUserRoleForChild?.(child.id) === USER_ROLES.CARE_PARTNER);
  const professionalChildren = children.filter(child => [USER_ROLES.CAREGIVER, USER_ROLES.THERAPIST].includes(getUserRoleForChild?.(child.id)));

  if (isMockMode) {
    return getE2EMockDashboardState();
  }

  return {
    user,
    theme,
    loading: loading || roleLoading,
    children,
    ownChildren,
    familyChildren,
    professionalChildren,
    allEntries,
    recentEntries,
    timelineSummary,
    incidents,
    expandedCards,
    expandedCategories,
    highlightedActions,
    showDailyCareModal,
    dailyCareAction,
    dailyCareChild,
    dailyCareEditEntry,
    showMoodSheet,
    moodSheetChild,
    moodSheetEntry,
    showSleepLogSheet,
    sleepLogChild,
    sleepLogEditEntry,
    sleepLogType,
    showFoodLogSheet,
    foodLogChild,
    foodLogEditEntry,
    showBathroomLogSheet,
    bathroomLogChild,
    bathroomLogEditEntry,
    showIncidentModal,
    incidentChild,
    incidentEditEntry,
    showFollowUpModal,
    followUpIncident,
    showPatternSuggestionModal,
    patternSuggestions,
    suggestionsChildId,
    showDailyHabitsModal,
    dailyHabitsInitialCategoryId,
    dailyHabitsChild,
    dailyHabitsEditEntry,
    showMedicalLogPanel,
    showSmartCaptureNoteSheet,
    medicalLogChild,
    medicalLogEditEntry,
    smartCaptureNoteChild,
    smartCaptureNoteEditEntry,
    showTimelineNoteEditSheet,
    timelineNoteEditChild,
    timelineNoteEditEntry,
    showQuickEntry,
    quickEntryMode,
    quickEntryEditEntry,
    selectedChild,
    entryType,
    quickEntryStep,
    quickEntryDate,
    quickEntryAutoSubmit,
    quickEntryInitialAction,
    quickEntryInitialPhotoFile,
    quickEntryInitialPhotoIntent,
    quickEntryInitialText,
    currentChildId,
    childrenWithAccess,
    roleLoading,
    USER_ROLES,
    isReadOnlyForChild,
    getUserRoleForChild,
    setCurrentChildId,
    setExpandedCategories,
    handleAddChild,
    handleInviteTeamMember,
    isCardExpanded,
    toggleCard,
    handleQuickDataEntry,
    handleEditChild,
    handleDeleteChild,
    handleDailyReport,
    handleMessages,
    handleGroupActionClick,
    getTypeConfig,
    formatTimeAgo,
    handleAddChildSuccess,
    handleEditChildSuccess,
    handleCloseDailyCareModal,
    handleCloseMoodSheet,
    handleCloseSleepLogSheet,
    handleCloseFoodLogSheet,
    handleCloseBathroomLogSheet,
    handleCloseMedicalLog,
    handleCloseSmartCaptureNoteSheet,
    handleCloseTimelineNoteEditSheet,
    handleCloseIncidentModal,
    handleCloseFollowUpModal,
    handleClosePatternSuggestionModal,
    handleCreateCustomCategories,
    setShowDailyHabitsModal,
    handleCloseDailyHabitsModal,
    handleTrack,
    handleOpenSleepLog,
    handleOpenFoodLog,
    handleOpenBathroomLog,
    handleOpenMedicalLog,
    handleEditMedicalLog,
    handleReviewChild,
    handleShowCareReport,
    handleQuickEntryComplete,
    handleTimelineNoteEditComplete,
    handleQuickEntrySkip,
    refreshDashboard,
  };
};
